import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    inject,
    signal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SearchFilterModel } from '../../models/SearchFilter-model';
import { LocalStorageService } from '../../service/LocalStorageService';
import { PureOriginalService } from '../../service/pure-original.service';
import { TuningNamesTablesService } from '../../service/Tuning-names-tables.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import {TableSourceEnum, VehicleDataEnum} from "../../models/VehicleData-model";
import {VehicleDataService} from "../../service/vehicleData.service";
import {formatFilterLabel} from "../format-filter-label";

@Component({
    selector: 'app-pure-originals-filters',
    templateUrl: './pure-originals-filters.html',
    styleUrls: ['./pure-originals-filters.css']
})
export class PureOriginalsFilters implements OnInit {

    public selectedOptions: SearchFilterModel[] = [];
    public groupKey: string = '';
    public tuningNames = signal<string[]>([]);
    public dynamicOptionValues: { [key: string]: string[] } = {};
    private liveSearchSubjectMap: { [key: string]: Subject<string> } = {};
    public vehicleDataService = inject(VehicleDataService);


    @Input() displayedColumns: string[] = [];
    public apiSearchableOptions: string[] = Object.values(VehicleDataEnum);

    public allAvailableOptions: string[] = [];

    @Output() pureOriginalsFilterClosed = new EventEmitter<boolean>();
    @Output() selectedFilters = new EventEmitter<SearchFilterModel[]>();
    @Output() selectedFiltersChange = new EventEmitter<SearchFilterModel[]>();
    @Output() filterCount = new EventEmitter<number>();

    private localStorageService = inject(LocalStorageService);
    private tuningNamesTableService = inject(TuningNamesTablesService);
    private pureOriginalService = inject(PureOriginalService);

    searchFilterForm = new FormGroup({});
    private vehicleEnumToInternalKeyMap: Record<VehicleDataEnum, string> = {
        [VehicleDataEnum.VEHICLE_PRODUCER]: 'vehicleProducer',
        [VehicleDataEnum.VEHICLE_MODEL]: 'vehicleModel',
        [VehicleDataEnum.VEHICLE_SERIES]: 'vehicleSeries',
        [VehicleDataEnum.VEHICLE_TYPE]: 'vehicleType'
,        [VehicleDataEnum.ECU_BUILD]: 'ecuBuild',
        [VehicleDataEnum.ECU_PRODUCER]: 'ecuProducer'
    };

    constructor(@Inject('LOCAL_STORAGE_KEY') private pureOriginalsFilter: string) {}

    ngOnInit(): void {
        this.getListOfCorrectTuningNames();

        const excludedKeys = ['vehicleProducer', 'vehicleSeries', 'ecuProducer', 'ecuBuild', 'vehicleModel'];
        const filteredDisplayedColumns = this.displayedColumns.filter(
            option => !excludedKeys.includes(option)
        );

        const savedFilters = this.localStorageService.getFilters(this.pureOriginalsFilter);

        const savedFilterOptions = savedFilters?.map(f => f.option) || [];

        // ✅ include missing options that are in saved filters
        const missingSavedOptions = savedFilterOptions.filter(
            opt => !Object.values(VehicleDataEnum).includes(opt as VehicleDataEnum) &&
                !filteredDisplayedColumns.includes(opt)
        );

        this.allAvailableOptions = [
            ...Object.values(VehicleDataEnum),
            ...filteredDisplayedColumns,
            ...missingSavedOptions // ensures we restore properly
        ];

        if (savedFilters && savedFilters.length > 0) {
            this.searchFilterForm = this.localStorageService.createFormGroupFromFilters(
                savedFilters,
                this.allAvailableOptions
            );
            Object.keys(this.searchFilterForm.controls).forEach(key => {
                const group = this.searchFilterForm.get(key) as FormGroup;
                this.setupFormGroup(group, key);
            });
            this.filterCount.emit(savedFilters.length);
        }


    }

    formatEnumKey(key: string): string {
        return formatFilterLabel(key);
    }

    getListOfCorrectTuningNames() {
        this.tuningNamesTableService.getListOfCorrectTuningNames().subscribe((response) => {
            this.tuningNames.set(response.correctTuningNames);
        });
    }

    addFormControl() {
        const controlKey = `control-${Object.keys(this.searchFilterForm.controls).length + 1}`;
        const control = new FormGroup({
            option: new FormControl(this.allAvailableOptions![0]),
            value: new FormControl('', Validators.required),
            hitRateMin: new FormControl(50),
            hitRateMax: new FormControl(100),
            search: new FormControl('')
        });

        this.setupFormGroup(control, controlKey);
        this.searchFilterForm.addControl(controlKey, control);
    }

    private setupFormGroup(group: FormGroup, key: string) {
        const optionControl = group.get('option');
        const valueControl = group.get('value');

        if (!optionControl || !valueControl) return;

        optionControl.valueChanges.subscribe(selected => {
            if (selected === 'hitRate') {
                valueControl.clearValidators();
            } else {
                valueControl.setValidators(Validators.required);
            }
            valueControl.updateValueAndValidity();
        });

        // Debounced value search for enums (trigger only if current option is from VehicleDataEnum)
        const subject = new Subject<string>();
        this.liveSearchSubjectMap[key] = subject;

        subject.pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(searchTerm => {
                const selected = optionControl.value;
                const trimmed = (searchTerm || '').trim();

                if (this.apiSearchableOptions.includes(selected) && trimmed.length >= 1) {
                    this.fetchVehicleData(selected, searchTerm, key);
                }
            });

        valueControl.valueChanges.subscribe(value => {
            subject.next(value);
        });
    }

    fetchVehicleData(option: string, search: string, formKey: string) {
        const searchLower = search.toLowerCase();

        if (option === VehicleDataEnum.VEHICLE_PRODUCER) {
            this.vehicleDataService.getCorrectVehicleProducers().subscribe({
                next: response => {
                    const filtered = (response.body || []).filter(item =>
                        item.toLowerCase().includes(searchLower)
                    );
                    this.dynamicOptionValues[`${formKey}_${option}`] = filtered;
                },
                error: err => console.error('Vehicle data fetch failed', err)
            });
            return;
        }

        if (option === VehicleDataEnum.VEHICLE_TYPE) {
            this.vehicleDataService.getCorrectVehicleTypes().subscribe({
                next: response => {
                    const filtered = (response.body || []).filter(item =>
                        item.toLowerCase().includes(searchLower)
                    );
                    this.dynamicOptionValues[`${formKey}_${option}`] = filtered;
                },
                error: err => console.error('Vehicle data fetch failed', err)
            });
            return;
        }

        const source = TableSourceEnum.PURE_ORIGINAL_BASE;
        this.vehicleDataService.searchForVehicleData(option as VehicleDataEnum, search, source).subscribe({
            next: response => {
                this.dynamicOptionValues[`${formKey}_${option}`] = response.body || [];
            },
            error: err => console.error('Vehicle data fetch failed', err)
        });
    }

    clearFilters() {
        this.localStorageService.clearFilters(this.pureOriginalsFilter);
        Object.keys(this.searchFilterForm.controls).forEach(key => {
            this.searchFilterForm.removeControl(key);
        });
        this.updateFilterCount();
        this.pureOriginalService.getPureOriginals();
    }

    closeModal() {
        this.pureOriginalsFilterClosed.emit(false);
    }

    onSubmit() {
        this.selectedOptions = [];

        for (const group in this.searchFilterForm.controls) {
            const rawValue = this.searchFilterForm.get(group)?.value;
            const option = rawValue?.option;
            const trimmedValue = (rawValue?.value as string)?.trim();

            // 🔁 Map enum value to internal key if needed
            const mappedOption = this.vehicleEnumToInternalKeyMap[option as VehicleDataEnum] || option;

            this.selectedOptions.push({
                option: mappedOption,
                value: trimmedValue,
                hitRateMin: rawValue?.hitRateMin,
                hitRateMax: rawValue?.hitRateMax
            });
        }

        this.localStorageService.saveFilters(this.pureOriginalsFilter, this.selectedOptions);
        this.selectedFilters.emit(this.selectedOptions);
        this.pureOriginalsFilterClosed.emit(false);
        this.updateFilterCount();
    }


    deleteSearchBar(key: string) {
        this.searchFilterForm.removeControl(key);
        delete this.liveSearchSubjectMap[key];

        const updatedFilters: SearchFilterModel[] = [];
        for (const groupKey in this.searchFilterForm.controls) {
            const filterGroup = this.searchFilterForm.get(groupKey)?.value;
            updatedFilters.push({
                option: filterGroup.option,
                value: filterGroup.value,
                hitRateMin: filterGroup.hitRateMin,
                hitRateMax: filterGroup.hitRateMax
            });
        }
        this.localStorageService.saveFilters(this.pureOriginalsFilter, updatedFilters);
        this.updateFilterCount();
        this.pureOriginalService.getPureOriginals();
    }

    hasFormControls(): boolean {
        return Object.keys(this.searchFilterForm.controls).length > 0;
    }

    updateFilterCount() {
        this.filterCount.emit(Object.keys(this.searchFilterForm.controls).length);
    }

    getDynamicOptions(formKey: string, option: string): string[] {
        return this.dynamicOptionValues[`${formKey}_${option}`] || [];
    }
}
